import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  StyleSheet,
  TextInput as RNTextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, {FadeIn, FadeOut, useAnimatedStyle} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useHeaderHeight} from '@react-navigation/elements';
import {useIsFocused, useNavigation} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';
import {useDebounce} from 'use-debounce';

import {useOptionalScrollCollapseContext} from '../context/ScrollCollapseContext';
import {useSearchDispatch, useSearchState} from '../context/SearchContext';
import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import {AppRoute} from '../models/AppRoute';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';

import {isSiteHeaderActive} from './SiteHeader/headerDenyList';
import Pressable from './ui-controls/PressableWithOpacityFeedback';
import Text from './ui-controls/Text';
import {Icon} from './Icon';
import RangeFilterOptions from './RangeFilterOptions';
import RangeSortOptions from './RangeSortOptions';

const PILL_BAR_HEIGHT = 48;
const PILL_BAR_MARGIN_BOTTOM = 8;

type StickyFilterPillBarProps = {
  /** DIS-1907: render the search control only; no Sort or Filter button. */
  searchOnly?: boolean;
  /** Scope name for the placeholder and "search all" link; overrides rangeStore's department. */
  scopeName?: string;
};

/**
 * Pill bar rendered as a flex sibling above the FlatList.
 * Bypasses Android stickyHeaderIndices touch/z-order bugs (RN #49179, #51763)
 * where list items with elevation render above and steal touches from sticky headers.
 */
const StickyFilterPillBar = observer(({searchOnly, scopeName}: StickyFilterPillBarProps) => {
  const {globalStore, rangeStore, filterStore} = useStores();
  const searchState = useSearchState();
  const dispatch = useSearchDispatch();
  const navigation = useNavigation();
  const searchInputRef = useRef<RNTextInput>(null);
  const [inputText, setInputText] = useState('');
  const [debouncedText, debounceControls] = useDebounce(inputText, 300);
  // Guard debounce dispatch: when user taps "search all", navigation pushes
  // SearchScreen on top of the Stack but DepartmentScreen stays mounted.
  // Without this guard, setInputText('') in handleSearchAllPress creates a
  // new 300ms debounce that fires SET_QUERY('') on the shared SearchContext,
  // overwriting SearchScreen's SET_QUERY('sparkling') and killing the query.
  const isFocused = useIsFocused();

  // DIS-1127: The collapse compensation logic was removed because the header is
  // now always absolute-positioned (headerTransparent: true on scroll-collapse
  // routes) and its safe-area inset is part of the header's own layout. The pill
  // bar's parent listWrapper handles the y=headerHeight offset via
  // useHeaderHeight(), so this component no longer needs to compensate.

  const activeContrastColor: string = theme.colour.background.button.contrastOnPrimary;
  const pillTextColor: string = theme.colour.background.button.contrastOnPrimary;

  useEffect(() => {
    if (isFocused) {
      dispatch({type: 'SET_QUERY', query: debouncedText});
    }
  }, [debouncedText, dispatch, isFocused]);

  const isExpanded = searchState.isSearchBarExpanded;

  // Search-only has no dropdown of its own, so a panel left open elsewhere must not restyle it.
  const isOpen = !searchOnly && globalStore.filtersVisible;
  const activeTab = globalStore.filterPanelDefaultTab;
  const activeFacetCount = Object.values(searchState.activeFacets).flat().length;
  // Don't count specials as a user-applied filter when it was set by the
  // Specials department scope (departmentId === '' + specials === true).
  // It only counts toward the badge when a user explicitly toggles it
  // within a non-specials department.
  const isSpecialsDepartmentScope = searchState.specials && searchState.departmentId === '';
  const activeFilterCount =
    activeFacetCount +
    (searchState.specials && !isSpecialsDepartmentScope ? 1 : 0) +
    (searchState.priceRange ? 1 : 0);
  const activeSortLabel =
    searchState.sortOptionId !== 'top_products'
      ? (filterStore.sortOptions.find(s => s.id === searchState.sortOptionId)?.name ??
        searchState.sortOptionId)
      : null;

  const toggleTab = (tab: 'SORT' | 'FILTER') => {
    if (isOpen && activeTab === tab) {
      globalStore.setFiltersVisible(false);
    } else {
      globalStore.setFilterPanelDefaultTab(tab);
      globalStore.setFiltersVisible(true);
    }
  };

  const isSortActive = isOpen && activeTab === 'SORT';
  const isFilterActive = isOpen && activeTab === 'FILTER';

  const handleSearchChipPress = () => {
    dispatch({type: 'SET_SEARCH_BAR_EXPANDED', expanded: true});
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleClearPress = () => {
    debounceControls.cancel();
    const wasEmpty = !inputText;
    setInputText('');
    dispatch({type: 'SET_QUERY', query: ''});
    if (wasEmpty) {
      dispatch({type: 'SET_SEARCH_BAR_EXPANDED', expanded: false});
    } else {
      searchInputRef.current?.focus();
    }
  };

  const handleBlur = () => {
    if (!inputText) {
      dispatch({type: 'SET_SEARCH_BAR_EXPANDED', expanded: false});
    }
  };

  const handleSearchAllPress = () => {
    debounceControls.cancel();
    navigation.navigate(AppRoute.NEW_SEARCH, {keywords: inputText});
    dispatch({type: 'SET_SEARCH_BAR_EXPANDED', expanded: false});
    setInputText('');
    // DO NOT dispatch SET_QUERY('') here.  The Department/SubDepartment
    // screen is still mounted at this point — clearing the query causes
    // useSearchLines to re-fetch without keywords (a wasted request that
    // can interfere with SearchScreen's display).  The query is cleared
    // naturally when the user returns: isFocused becomes true, the
    // debounce effect fires SET_QUERY('') from the empty inputText.
  };

  const departmentName =
    scopeName ??
    rangeStore.selectedSubDepartment?.name ??
    rangeStore.selectedDepartment?.name ??
    null;

  const {isWebDesktopScreen: isDesktop} = useWebScreenDimensions();
  const isSearchActiveWithScope = isExpanded && inputText.length > 0 && !!departmentName;
  // Mobile: hide text labels (icon-only); Desktop: always show labels
  const hideLabels = !isDesktop;

  // Map sort option id → pictographic icon + direction for compact mobile indicator
  const sortIndicator: {icon: string; direction: 'asc' | 'desc'; selfDescribing?: boolean} | null =
    (() => {
      const id = searchState.sortOptionId;
      if (!id || id === 'top_products') return null;
      const map: Record<
        string,
        {icon: string; direction: 'asc' | 'desc'; selfDescribing?: boolean}
      > = {
        price: {icon: 'coins', direction: 'asc'},
        price_descending: {icon: 'coins', direction: 'desc'},
        name: {icon: 'arrow-down-a-z', direction: 'asc', selfDescribing: true},
        name_descending: {icon: 'arrow-down-z-a', direction: 'desc', selfDescribing: true},
        unit_price: {icon: 'tag', direction: 'asc'},
        discount_percent: {icon: 'receipt', direction: 'desc'},
        discount_cents: {icon: 'piggy-bank', direction: 'desc'},
      };
      return map[id] ?? {icon: 'bars-staggered', direction: 'asc'};
    })();

  const searchPlaceholder = departmentName
    ? `Search in ${departmentName}...`
    : 'Search products...';

  return (
    <View style={styles.outerContainer} testID="sticky-filter-bar">
      <View style={[styles.pillBar, isOpen && styles.pillBarOpen]}>
        {/* Search chip / expanded input */}
        {isExpanded ? (
          <View style={styles.searchInputContainer} testID="sticky-filter-bar-search-expanded">
            <Icon name="magnifying-glass" size={14} color={theme.colour.text.heading.primary} />
            <RNTextInput
              ref={searchInputRef}
              style={styles.searchInput}
              value={inputText}
              onChangeText={setInputText}
              onBlur={handleBlur}
              placeholder={searchPlaceholder}
              placeholderTextColor="#999"
              returnKeyType="search"
              autoCorrect={false}
              testID="sticky-filter-bar-search-input"
            />
            <Pressable onPress={handleClearPress} testID="sticky-filter-bar-search-clear">
              <Icon name="xmark" size={14} color={theme.colour.text.heading.primary} />
            </Pressable>
          </View>
        ) : (
          <Pressable
            onPress={handleSearchChipPress}
            style={styles.searchChipButton}
            testID="sticky-filter-bar-search">
            <Icon name="magnifying-glass" size={14} color={theme.colour.text.heading.primary} />
            <Text style={styles.buttonText}>Search</Text>
          </Pressable>
        )}

        {/* Inline search scope — desktop only, replaces overlay */}
        {isDesktop && isSearchActiveWithScope ? (
          <Pressable
            onPress={handleSearchAllPress}
            style={styles.searchScopeInline}
            testID="sticky-filter-bar-search-scope-inline">
            <Text style={styles.searchScopeText} numberOfLines={1}>
              in {departmentName} — <Text style={styles.searchScopeLinkText}>search all</Text>
            </Text>
          </Pressable>
        ) : null}

        {searchOnly ? null : (
          <>
            <View style={styles.dividerWrapper}>
              <View style={styles.dividerLine} />
              <View style={styles.dividerHighlight} />
            </View>

            <Pressable
              onPress={() => toggleTab('SORT')}
              style={[styles.button, isSortActive && styles.buttonActive]}
              testID="sticky-filter-bar-sort">
              <Icon
                name="arrow-down-short-wide"
                size={14}
                color={isSortActive ? activeContrastColor : theme.colour.text.heading.primary}
              />
              {!hideLabels ? (
                <Text
                  style={[
                    styles.buttonText,
                    isSortActive && styles.buttonTextActive,
                    isSortActive && {color: activeContrastColor},
                  ]}>
                  Sort
                </Text>
              ) : null}
              {activeSortLabel ? (
                hideLabels && sortIndicator ? (
                  <View style={styles.sortIndicatorPill}>
                    {!sortIndicator.selfDescribing ? (
                      <Icon
                        name={sortIndicator.direction === 'asc' ? 'arrow-up' : 'arrow-down'}
                        size={10}
                        color={pillTextColor}
                      />
                    ) : null}
                    <Icon name={sortIndicator.icon} size={14} color={pillTextColor} />
                  </View>
                ) : (
                  <View style={styles.sortChip}>
                    <Text style={[styles.sortChipText, {color: pillTextColor}]} numberOfLines={1}>
                      {activeSortLabel}
                    </Text>
                  </View>
                )
              ) : null}
              {!hideLabels ? (
                isSortActive ? (
                  <Icon name="chevron-up" size={10} color={activeContrastColor} />
                ) : (
                  <Icon name="chevron-down" size={10} color={theme.colour.text.heading.primary} />
                )
              ) : null}
            </Pressable>

            <View style={styles.dividerWrapper}>
              <View style={styles.dividerLine} />
              <View style={styles.dividerHighlight} />
            </View>

            <Pressable
              onPress={() => toggleTab('FILTER')}
              style={[styles.button, isFilterActive && styles.buttonActive]}
              testID="sticky-filter-bar-filter">
              <Icon
                name="sliders"
                size={14}
                color={isFilterActive ? activeContrastColor : theme.colour.text.heading.primary}
              />
              {!hideLabels ? (
                <Text
                  style={[
                    styles.buttonText,
                    isFilterActive && styles.buttonTextActive,
                    isFilterActive && {color: activeContrastColor},
                  ]}>
                  Filter
                </Text>
              ) : null}
              {activeFilterCount > 0 ? (
                <View style={styles.badge}>
                  <Text style={[styles.badgeText, {color: pillTextColor}]}>
                    {activeFilterCount}
                  </Text>
                </View>
              ) : null}
              {!hideLabels ? (
                isFilterActive ? (
                  <Icon name="chevron-up" size={10} color={activeContrastColor} />
                ) : (
                  <Icon name="chevron-down" size={10} color={theme.colour.text.heading.primary} />
                )
              ) : null}
            </Pressable>
          </>
        )}
      </View>

      {/* "Search all" overlay — mobile only (desktop uses inline scope) */}
      {!isDesktop && isExpanded && inputText.length > 0 && departmentName ? (
        <View style={styles.searchAllLink} testID="sticky-filter-bar-search-all">
          <Text style={styles.searchAllText}>Searching in {departmentName} — </Text>
          <Pressable onPress={handleSearchAllPress} testID="sticky-filter-bar-search-all-link">
            <Text style={styles.searchAllLinkText}>search all products instead</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
});

/**
 * Dropdown overlay — rendered at screen level (outside the FlatList)
 * to avoid Android touch/z-ordering issues.
 */
const StickyFilterDropdown = observer(() => {
  const {globalStore} = useStores();
  const isOpen = globalStore.filtersVisible;
  const activeTab = globalStore.filterPanelDefaultTab;
  const {height: windowHeight} = useWindowDimensions();
  const dropdownMaxHeight = windowHeight * 0.55;

  // DIS-1127: On routes with headerTransparent:true, listWrapper sits under
  // the header. Position the panel at `headerHeight + PILL_BAR_HEIGHT` so it
  // renders directly below the pill bar (which is itself positioned at
  // top:headerHeight). Animate translateY in sync with the header collapse
  // so the panel follows the pill bar when the user scrolls.
  const headerHeight = useHeaderHeight();
  const insets = useSafeAreaInsets();
  const scrollCollapseCtx = useOptionalScrollCollapseContext();

  // DIS-1127: single Animated.View for the panel. Splitting it (two
  // nested Animated.Views) changed the Yoga parent chain and silently
  // broke DIS-1076's sub-filter FlatList scrolling on Android. Keep this
  // as ONE view and use `FadeIn`/`FadeOut` (opacity-only layout animations,
  // no transform component) so the entering/exiting animation does not
  // fight with our useAnimatedStyle translateY.
  const panelStyle = useMemo(
    () => [
      styles.dropdownPanel,
      {top: headerHeight + PILL_BAR_HEIGHT, maxHeight: dropdownMaxHeight},
    ],
    [headerHeight, dropdownMaxHeight],
  );

  const backdropStaticStyle = useMemo(() => [styles.backdrop, {top: headerHeight}], [headerHeight]);

  // DIS-1840: with SiteHeader active the header box does not move; follow the visible bottom
  // of SiteHeader instead. Native and HEADER-denied web keep the formula below.
  const siteHeaderActive = isSiteHeaderActive();
  const panelAnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    if (!scrollCollapseCtx) return {transform: [{translateY: 0}]};
    if (siteHeaderActive) {
      return {transform: [{translateY: -scrollCollapseCtx.siteHeaderCompactOffset.get()}]};
    }
    // Clamp to (headerHeight - safeAreaTop) so the panel never translates
    // into the status bar region (Android touch dead zone).
    const maxCollapse = Math.max(0, scrollCollapseCtx.headerHeight.get() - insets.top);
    const offset = Math.min(scrollCollapseCtx.scrollY.get(), maxCollapse);
    return {transform: [{translateY: -offset}]};
  });

  const close = () => globalStore.setFiltersVisible(false);

  if (!isOpen) return null;

  return (
    <>
      {/* Scrim overlay — dims background below the header & closes dropdown
          on tap outside. Starts at headerHeight so the transparent header
          stays visible and interactive while filters are open. */}
      <Animated.View
        entering={FadeIn.duration(200)}
        exiting={FadeOut.duration(150)}
        style={backdropStaticStyle}>
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          activeOpacity={1}
          onPress={close}
          testID="sticky-filter-backdrop"
        />
      </Animated.View>

      {/* Dropdown panel — single Animated.View. Using FadeIn/FadeOut
          (opacity-only) on the SAME view as our useAnimatedStyle transform
          is safe because FadeIn/FadeOut do not animate transform, so
          Reanimated does not warn and our translateY is not overwritten. */}
      <Animated.View
        entering={FadeIn.duration(200)}
        exiting={FadeOut.duration(150)}
        style={[panelStyle, panelAnimatedStyle]}
        testID="sticky-filter-dropdown">
        {activeTab === 'SORT' ? <RangeSortOptions /> : null}
        {activeTab === 'FILTER' ? <RangeFilterOptions /> : null}
      </Animated.View>
    </>
  );
});

export default StickyFilterPillBar;
export {StickyFilterDropdown};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 15,
    elevation: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  // Rendered as a flex sibling above the FlatList (not inside it).
  // Avoids Android stickyHeaderIndices touch bugs (RN #49179, #51763).
  // Header offset is applied by the parent screen's listWrapper via useHeaderHeight().
  outerContainer: {
    backgroundColor: '#ffffff',
    marginBottom: PILL_BAR_MARGIN_BOTTOM,
  },
  // Sort/Filter bar — subtle radius, full width
  pillBar: {
    flexDirection: 'row',
    alignItems: 'center',
    height: PILL_BAR_HEIGHT,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.1,
    shadowRadius: 6,
  },
  // When dropdown is open: flatten bottom corners (top already flat)
  pillBarOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minWidth: 44,
    gap: 6,
    paddingHorizontal: 12,
  },
  // Active state: brand button-primary background (consistent blue across all brands)
  buttonActive: {
    backgroundColor: theme.colour.background.button.primary,
  },
  // Full-height divider with inset shadow groove
  dividerWrapper: {
    width: 2,
    height: '100%',
    flexDirection: 'row',
  },
  dividerLine: {
    width: 1,
    height: '100%',
    backgroundColor: '#d0d0d0',
  },
  dividerHighlight: {
    width: 1,
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.8)',
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colour.text.heading.primary,
  },
  buttonTextActive: {
    fontWeight: '700',
  },
  badge: {
    backgroundColor: theme.colour.background.button.primary,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  sortChip: {
    backgroundColor: theme.colour.background.button.primary,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    maxWidth: 120,
  },
  sortChipText: {
    fontSize: 11,
    fontWeight: '700',
  },
  // Mobile compact sort indicator: [icon ↑] or [icon ↓] in branded pill
  sortIndicatorPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colour.background.button.primary,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 4,
    gap: 3,
  },
  // Inline search scope text (desktop, between input and sort/filter)
  searchScopeInline: {
    flexShrink: 1,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  searchScopeText: {
    fontSize: 12,
    color: theme.colour.text.heading.primary,
  },
  searchScopeLinkText: {
    color: theme.colour.background.button.primary,
    textDecorationLine: 'underline' as const,
  },
  // Search chip (collapsed)
  searchChipButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    gap: 6,
    paddingHorizontal: 12,
  },
  // Search input (expanded)
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    paddingHorizontal: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    height: '100%',
    fontSize: 14,
    color: theme.colour.text.heading.primary,
    paddingVertical: 6,
    // @ts-expect-error -- web-only: suppress browser default focus outline
    outlineStyle: 'none',
  },
  // "Search all products" link below pill bar
  searchAllLink: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f8f8f8',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: '#e0e0e0',
  },
  searchAllText: {
    fontSize: 12,
    color: theme.colour.text.heading.primary,
  },
  searchAllLinkText: {
    color: theme.colour.background.button.primary,
    textDecorationLine: 'underline',
  },
  // Dropdown panel — SINGLE Animated.View. Positioned absolutely at
  // (headerHeight + PILL_BAR_HEIGHT) below the pill bar (see component
  // for details). Splitting this into outer+inner broke DIS-1076 sub-
  // filter scrolling on Android because it changed the Yoga parent chain;
  // kept as one view with panelAnimatedStyle applied directly.
  dropdownPanel: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 20,
    backgroundColor: 'white',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 9,
    overflow: 'hidden',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
});
